import DownloadIcon from "@mui/icons-material/Download";
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

import { Invoice } from "../types/entities";

export interface InvoiceHistoryListProps {
  invoices: Invoice[];
  language: SupportedLanguage;
}

/**
 * Invoice history table for the Pricing/Billing tab (#2794), consuming
 * `GET /subscriptions/invoices` (#2791). Purely informational — a date, an
 * amount, a status chip, and a download link per invoice — with no
 * purchase/upgrade/cancel action anywhere, so it renders identically for
 * the Owner and for a read-only Admin viewer; there is nothing here to
 * gate on `isOwner` (unlike `PricingTab`'s "Select Plan"/"Change
 * Plan"/"Cancel subscription" buttons).
 *
 * The download link points at this app's own signed, short-lived redirect
 * endpoint (`invoice.pdfUrl`, see `InvoiceDto` on the backend) — never a
 * direct Stripe URL — so a plain anchor tag is safe and sufficient; no
 * authenticated fetch/blob handling is needed here.
 */
export function InvoiceHistoryList({
  invoices,
  language,
}: InvoiceHistoryListProps) {
  if (invoices.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="invoice-history-empty"
      >
        {getLocalizedMessage(
          language,
          "homePortalPage.tabs.pricePlans.invoiceHistory.empty"
        )}
      </Typography>
    );
  }

  const locale = language === "de" ? "de-DE" : "en-US";

  return (
    <TableContainer data-testid="invoice-history-list">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.invoiceHistory.date"
              )}
            </TableCell>
            <TableCell>
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.invoiceHistory.amount"
              )}
            </TableCell>
            <TableCell>
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.invoiceHistory.status"
              )}
            </TableCell>
            <TableCell align="right">
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.invoiceHistory.download"
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow
              key={invoice.id}
              data-testid={`invoice-row-${invoice.id}`}
            >
              <TableCell>
                {new Date(invoice.date).toLocaleDateString(locale, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </TableCell>
              <TableCell>
                {invoice.amountEur.toLocaleString(locale, {
                  style: "currency",
                  currency: invoice.currency.toUpperCase(),
                })}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={getLocalizedMessage(
                    language,
                    `homePortalPage.tabs.pricePlans.invoiceHistory.statuses.${invoice.status}`
                  )}
                />
              </TableCell>
              <TableCell align="right">
                <Tooltip
                  title={getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.invoiceHistory.download"
                  )}
                >
                  <IconButton
                    component="a"
                    href={invoice.pdfUrl}
                    target="_blank"
                    rel="noopener"
                    size="small"
                    data-testid={`invoice-pdf-link-${invoice.id}`}
                    aria-label={getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.pricePlans.invoiceHistory.download"
                    )}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
